import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../translation/i18n';

import useWeatherInfo from '../../hooks/useWeatherInfo';
import styled from 'styled-components';
import { PulseLoader } from 'react-spinners';
import { getWeatherIcon as getWeatherIconUtil } from '../../utils/weatherUtils';
import IssueGuide from '../IssueGuide';
import { WEATHER_ERROR_MESSAGE } from '../../_constants';

const animateWeather = (weather: string) => {
  switch (weather) {
    case 'rain': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 95) {
        const randoHundo = Math.floor(Math.random() * 100);
        const randoFiver = Math.floor(Math.random() * 5) + 2;
        increment += randoFiver;

        const dropStyle = {
          left: `${increment}%`,
          animationDelay: `0.${randoHundo}s`,
          animationDuration: `0.8${randoHundo}s`,
        };

        newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
      }

      return <div className='container'>{newDrops}</div>;
    }

    case 'shower': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 95) {
        const randoHundo = Math.floor(Math.random() * 100);
        const randoFiver = Math.floor(Math.random() * 5) + 2;
        increment += randoFiver;

        const dropStyle = {
          left: `${increment}%`,
          animationDelay: `0.${randoHundo}s`,
          animationDuration: `0.3${randoHundo}s`,
        };

        newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
      }

      return <div className='container'>{newDrops}</div>;
    }

    case 'sleet': {
      const newDrops: any[] = [];
      let increment = 0;
      const types = ['raindrop', 'snowdrop'];

      while (increment < 100) {
        const randomType = types[Math.floor(Math.random() * types.length)];

        const randomDelay = (Math.random() * 5).toFixed(2);
        const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
        const randomLeftRight = Math.floor(Math.random() * 4);

        const snowSize = Math.floor(Math.random() * 5);
        const snowOpacity = (Math.random() * 1).toFixed(2);
        const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

        increment += randomUnit;

        let animationDuration;
        switch (randomType) {
          case 'raindrop':
            animationDuration = `${2 + Math.random()}s`;
            break;
          case 'snowdrop':
            animationDuration = `${5 + Math.random()}s`;
            break;
          default:
            animationDuration = `${5 + Math.random()}s`;
            break;
        }

        const rainParticle = {
          left: `${increment}%`,
          animationDelay: `${+randomDelay}s`,
          animationDuration,
          '--random-left-right': `${randomLeftRight}vw`,
        } as React.CSSProperties;

        const snowParticle = {
          width: `${snowSize}px`,
          height: `${snowSize}px`,
          borderRadius: `${snowSize}px`,
          backgroundColor: `#fff`,
          opacity: `${Math.max(+snowOpacity, 0.3)}`,
          left: `${increment}%`,
          animationDelay: `${+randomDelay}s`,
          animationDuration: `${randomDuration}s`,
          '--random-left-right': `${randomLeftRight}vw`,
        } as React.CSSProperties;

        newDrops.push(<div className={randomType} style={randomType === 'raindrop' ? rainParticle : snowParticle} key={increment} />);
      }

      return <div className='container'>{newDrops}</div>;
    }

    case 'snow': {
      const newDrops: any[] = [];
      let increment = 0;
      while (increment < 100) {
        const randomDelay = (Math.random() * 5).toFixed(2);
        const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
        const randomLeftRight = Math.floor(Math.random() * 4);

        const snowSize = Math.floor(Math.random() * 5);
        const snowOpacity = (Math.random() * 1).toFixed(2);
        const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

        increment += randomUnit;

        const snowParticle = {
          width: `${snowSize}px`,
          height: `${snowSize}px`,
          borderRadius: `${snowSize}px`,
          backgroundColor: `#fff`,
          opacity: `${Math.max(+snowOpacity, 0.3)}`,
          left: `${increment}%`,
          animationDelay: `${+randomDelay}s`,
          animationDuration: `${randomDuration}s`,
          '--random-left-right': `${randomLeftRight}vw`,
        };

        newDrops.push(<div className='snowdrop' style={snowParticle as React.CSSProperties} key={increment} />);
      }

      return <div className='container'>{newDrops}</div>;
    }
    default: {
      return null;
    }
  }
};

const getWeatherIconComponent = (weather: string, size: number = 48) => {
  return getWeatherIconUtil(weather, size);
};

const getTimeStatus = (): string => {
  const currentHour = dayjs().hour();

  if (currentHour >= 0 && currentHour < 6) return 'morning';
  if (currentHour >= 6 && currentHour < 18) return 'afternoon';
  return 'evening';
};

export const WeatherInfo = ({ name, refetchInterval }: { name: string; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const {
    curWeather,
    threeDaysWeather,
    openDayWeather,
    openThreeDaysWeather,
    refetchCurrentWeather,
    refetchThreeDaysWeather,
    refetchOpenDayWeather,
    refetchOpenThreeDaysWeather,
    isErrorCurrentWeather,
    isErrorThreeDaysWeather,
  } = useWeatherInfo({
    autoRefresh: isSiteDashboard,
  });
  const { t } = useTranslation();
  const todayjs = dayjs();
  let format = todayjs.format(`MM월 DD일 dddd`);
  if (i18n.language === 'en-US') format = todayjs.locale('en').format(`MMMM D dddd`);
  else format = todayjs.locale('ko').format(`MM월 DD일 dddd`);

  const days = Array.from({ length: 3 }).map((_, index) => {
    const targetDay = dayjs().add(index, 'day'); // +1 to start from tomorrow
    return {
      order: index,
      date: targetDay.date(),
      day: targetDay.format('ddd'),
    };
  });

  /**
   * 수정자 : 한영광
   * 수정일자 : 2023.12.01
   * 수정내용 : 로컬스토리지에 날씨 데이터를 저장하고, 재 로딩 시 날씨 API 요청에 대한 응답이 오기 전에 먼저 보여주도록 처리하여 로딩이 느린 부분 개선
   */
  const after2DaysLocalStorage = window.localStorage.getItem('after2Days');
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeather');
  const localAfter2Days = after2DaysLocalStorage ? JSON.parse(after2DaysLocalStorage) : days;
  const localCurrentWeather = currentWeatherLocalStorage
    ? JSON.parse(currentWeatherLocalStorage)
    : { temp: '0', icon: '-', wsd: '0', sky: '-', maxTemp: '0', minTemp: '0', rainPct: '0', pm10: '0', pm25: '0' };
  const [after2Days, setAfter2Days] = useState<any[]>(localAfter2Days);
  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);
  const [showSvg, setShowSvg] = useState(false);

  useEffect(() => {
    if (!isErrorCurrentWeather && curWeather) {
      const { sky, tmp, wsd } = curWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd }));
    }
  }, [isErrorCurrentWeather, curWeather]);

  useEffect(() => {
    if (!isErrorThreeDaysWeather && threeDaysWeather) {
      setAfter2Days(threeDaysWeather);
    }
  }, [isErrorThreeDaysWeather, threeDaysWeather]);

  useEffect(() => {
    if (isErrorCurrentWeather && openDayWeather) {
      const { sky, tmp, wsd, todayTmx, todayTmn } = openDayWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd, maxTemp: todayTmx, minTemp: todayTmn }));
    }
  }, [isErrorCurrentWeather, openDayWeather]);

  useEffect(() => {
    if (!threeDaysWeather && openThreeDaysWeather) {
      setAfter2Days(openThreeDaysWeather);
    }
  }, [threeDaysWeather, openThreeDaysWeather]);

  useEffect(() => {
    if (after2Days) {
      window.localStorage.setItem('after2Days', JSON.stringify(after2Days));
    }
    if (currentWeather) {
      window.localStorage.setItem('currentWeather', JSON.stringify(currentWeather));
    }
  }, [after2Days, currentWeather]);

  const onClickRefresh = () => {
    refetchCurrentWeather();
    refetchThreeDaysWeather();
    refetchOpenDayWeather();
    refetchOpenThreeDaysWeather();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSvg(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // if (isErrorCurrentWeather) return <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} />;

  return (
    <div className={`weatherTodayInfo ${isSiteDashboard ? 'monitor-weather' : ''}`} style={isErrorCurrentWeather ? { position: 'relative' } : { position: 'static' }}>
      {/* {isErrorCurrentWeather ? (
        <div className='weatherError'>
          <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
        </div>
      ) : null} */}
      <div className='widget-header'>
        <div className='widget-title flex-between '>
          {!isSiteDashboard && (
            <>
              <span>{t(name)}</span>
              <div className='refresh-btn white' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className='widget-body'>
        <div className='weatherToday' style={{ color: '#fff' }}>
          <div className='weatherInfo'>
            <div>
              <div>{t('오늘의 날씨')}</div>
              <div className='weather-dateInfo'>{format}</div>
            </div>
            <div>{getWeatherIconComponent(currentWeather.sky, 160)}</div>
          </div>
          <div className='weatherAnimationWrapper'>{animateWeather(currentWeather.sky)}</div>
        </div>
        <div className='weatherTodayDetail'>
          <div className='weatherBoxGroup'>
            <div className='weatherBox text'>
              <div>
                <dt>{t('강수확률')}</dt>
                <dd>{after2Days[0].rainPct}%</dd>
              </div>
              <div>
                <dt>{t('미세먼지')}</dt>
                <dd>{after2Days[0].pm10}㎍/㎥</dd>
              </div>
              <div>
                <dt>{t('풍속')}</dt>
                <dd>{currentWeather.wsd}m/s</dd>
              </div>
            </div>
            <div className='weatherBox text'>
              <div>
                <dt>{t('오전')}</dt>
                <dd>{after2Days[0].morTmp}°</dd>
              </div>
              <div>
                <dt>{t('정오')}</dt>
                <dd>{after2Days[0].aftTmp}°</dd>
              </div>
              <div>
                <dt>{t('오후')}</dt>
                <dd>{after2Days[0].dinTmp}°</dd>
              </div>
            </div>
            <div className='weatherBox icon'>
              <div>
                <dt>{t('내일')}</dt>
                <div className='weatherBox-details'>
                  <div className='lowest'>
                    {t('최저')} <span>{after2Days[1].minTemp}</span>°
                  </div>
                  <div className='highest'>
                    {t('최고')} <span>{after2Days[1].maxTemp}</span>°
                  </div>
                </div>
              </div>
              <dd>
                <div>{getWeatherIconComponent(after2Days[1].sky, 64)}</div>
              </dd>
            </div>
            <div className='weatherBox icon'>
              <div>
                <dt>{t('모레')}</dt>
                <div className='weatherBox-details'>
                  <div className='lowest'>
                    {t('최저')} <span>{after2Days[2].minTemp}</span>°
                  </div>
                  <div className='highest'>
                    {t('최고')} <span>{after2Days[2].maxTemp}</span>°
                  </div>
                </div>
              </div>
              <dd>
                <div>{getWeatherIconComponent(after2Days[2].sky, 64)}</div>
              </dd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const WeatherWeekInfo = ({ refetchInterval }: { refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { t } = useTranslation();
  const [showSvg, setShowSvg] = useState(false);
  const {
    curWeather,
    weekWeather,
    shortAdrs,
    openDayWeather,
    refetchCurrentWeather,
    refetchWeekWeather,
    refetchOpenDayWeather,
    refetchOpenThreeDaysWeather,
    isErrorCurrentWeather,
    isErrorWeekWeather,
  } = useWeatherInfo({
    autoRefresh: isSiteDashboard,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSvg(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const days = Array.from({ length: 6 }).map((_, index) => {
    const targetDay = dayjs().add(index + 1, 'day'); // +1 to start from tomorrow
    return {
      order: index,
      date: targetDay.date(),
      day: targetDay.format('ddd'),
      maxTemp: '0',
      minTemp: '0',
      sky: '-',
    };
  });

  type IDays = {
    order: number;
    date: number;
    day: string;
    minTemp?: string;
    maxTemp?: string;
    sky: string;
    icon?: any;
  };
  const after6DaysLocalStorage = window.localStorage.getItem('after6Days');
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeatherW');
  const localAfter6Days = after6DaysLocalStorage ? JSON.parse(after6DaysLocalStorage) : days;
  const localCurrentWeather = currentWeatherLocalStorage
    ? JSON.parse(currentWeatherLocalStorage)
    : { temp: '0', icon: '-', wsd: '0', sky: '-', maxTemp: '0', minTemp: '0', rainPct: '0', pm10: '0', pm25: '0', adrs: '-' };
  const [after6Days, setAfter6Days] = useState<IDays[]>(localAfter6Days);
  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);

  useEffect(() => {
    if (!isErrorCurrentWeather && curWeather) {
      const { sky, tmp, wsd } = curWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd }));
    }
  }, [isErrorCurrentWeather, curWeather]);

  useEffect(() => {
    if (weekWeather) {
      const status = getTimeStatus();
      setCurrentWeather((prev: any) => ({
        ...prev,
        minTemp: weekWeather.todayTmn,
        maxTemp: weekWeather.todayTmx,
        rainPct: status === 'morning' ? weekWeather.morPop : status === 'evening' ? weekWeather.dinPop : weekWeather.aftPop,
      }));

      setAfter6Days(weekWeather.week);
    }
  }, [weekWeather]);

  useEffect(() => {
    if (isErrorCurrentWeather && openDayWeather) {
      const { sky, tmp, wsd, todayTmx, todayTmn } = openDayWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd, maxTemp: todayTmx, minTemp: todayTmn }));
    }
  }, [isErrorCurrentWeather, openDayWeather]);

  // 기존 날씨 주간 API 쿼리 에러시 시간 업데이트
  useEffect(() => {
    if (isErrorWeekWeather) {
      // 로컬 스토리지에 값이 있으면 그 값을 활용
      if (after6DaysLocalStorage) {
        const localData = JSON.parse(after6DaysLocalStorage);
        const dateSet = days.map((day) => {
          const findIdx = localData.findIndex((localDay: any) => localDay.date === day.date);
          return findIdx !== -1 ? { ...day, sky: localData[findIdx].sky, minTemp: localData[findIdx].minTemp, maxTemp: localData[findIdx].maxTemp } : day;
        });
        setAfter6Days(dateSet);
      } else {
        // 로컬 스토리지 값이 없으면 시간 업데이트
        setAfter6Days(days);
      }
    }
  }, [isErrorWeekWeather]);

  useEffect(() => {
    if (after6Days) {
      window.localStorage.setItem('after6Days', JSON.stringify(after6Days));
    }
    if (currentWeather) {
      window.localStorage.setItem('currentWeatherW', JSON.stringify(currentWeather));
    }
  }, [after6Days, currentWeather]);

  const onClickRefresh = () => {
    refetchCurrentWeather();
    refetchWeekWeather();
    refetchOpenDayWeather();
    refetchOpenThreeDaysWeather();
  };

  return (
    <div className={`weatherWeekInfo ${isSiteDashboard ? 'monitor-weather' : ''}`} style={isErrorCurrentWeather || isErrorWeekWeather ? { position: 'relative' } : { position: 'static' }}>
      {/* {isErrorCurrentWeather || isErrorWeekWeather ? (
        <div className='weatherError'>
          <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
        </div>
      ) : null} */}
      <div className='widget-header'>
        <div className='widget-title flex-between '>
          {!isSiteDashboard && (
            <>
              <span>{t('기상 정보')}</span>
              <div className='refresh-btn white' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className='widget-body'>
        <div className='weatherToday notWeek' style={{ color: '#fff' }}>
          <div className='weatherInfo'>
            <div>
              <div>
                {t('주간 날씨')} • {shortAdrs}
              </div>
              <div className='weatherBox text'>
                <div>
                  <dt>
                    {t('현재')} {currentWeather.tmp}°
                  </dt>
                </div>
                <div>
                  <dt>
                    {t('최저')} {Math.floor(Number(currentWeather.minTemp))}°
                  </dt>
                  <dt>
                    {t('최고')} {Math.floor(Number(currentWeather.maxTemp))}°
                  </dt>
                </div>
                <div>
                  <dt>
                    {t('강수확률')} {currentWeather.rainPct}%
                  </dt>
                </div>
              </div>
            </div>
            <div className='weather-icon'>{getWeatherIconComponent(currentWeather.sky, 160)}</div>
          </div>
          <div className='weatherAnimationWrapper'>{animateWeather(currentWeather.sky)}</div>
        </div>
        <div className='weatherWeek'>
          {after6Days.map(({ date, day, maxTemp, minTemp, sky }: IDays) => {
            return (
              <div key={date}>
                <div className='date'>
                  <div>{t(day)}</div>
                  <div>{date}</div>
                </div>
                <div className='weatherWeekIcon'>{getWeatherIconComponent(sky, 48)}</div>
                <div className='weatherWeekTemp'>
                  <div className='lowest'>{minTemp}°</div>
                  <div className='highest'>{maxTemp}°</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const WeatherCurrentInfoRoot = styled.div`
  display: flex !important;
  align-items: center !important;
  flex-direction: row !important;
  /* padding: 0 2rem; */
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: #3597f5;

  .weatherError {
    position: absolute;
    width: 100%;
    height: auto;
    top: 0;
    > div {
      width: inherit;
      > p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: inherit;
        padding: 0.25rem 0;
        color: #fff !important;
        background-color: rgba(0, 0, 0, 0.4);
        border-radius: 0;
        gap: 0.25rem;

        > span.material-symbols-rounded {
          font-size: 20px;
        }
      }
    }
  }

  & {
    > .info {
      width: 30%;
      padding: 0 2rem;

      > .maxAndMin > .details,
      > .details {
        &.location {
          padding-bottom: 1.5rem;

          > .label {
            display: none;
          }

          > .value {
            font-size: 2rem;
            font-weight: 700;
          }
        }
        > .material-symbols-rounded {
          font-size: 1.625rem;
        }
        > .label,
        > .value {
          font-size: 1.125rem;
        }
      }
    }
    > .large-info-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 70%;
      z-index: 10;

      .large-weather-top {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1.75rem;
        padding: 0 0 1.5rem 0;

        .large-weather-animation {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 182px;

          .rotating-sun {
            svg {
              animation: rotation 10s infinite linear;
            }
          }
        }

        .tempNow {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 0.5rem;
          color: var(--board);

          > span {
            font-size: 1.125rem;
            font-weight: 500;

            b {
              font-size: 4.25rem;
              font-weight: 800;
            }

            &.large-date-format {
              font-size: 0.875rem;
              font-weight: 400;
            }
          }
        }
      }

      .large-weather-bottom {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;

        .bottom-weather-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 210px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          padding: 1.25rem 1.125rem;

          .large-item-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 72px;
            height: 46px;

            svg {
              width: 100%;
            }
          }

          .large-item-details {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            gap: 0.5rem;

            .large-item-title {
              color: rgba(255, 255, 255, 0.8);
              font-size: 0.875rem;
            }

            .large-item-info {
              gap: 0.5rem;

              > span {
                font-size: 24px;
                font-weight: 600;
                color: #fff;

                &:last-child {
                  color: rgba(255, 255, 255, 0.6);
                }
              }
            }
          }
        }
      }
    }
    > .weatherAnimationWrapper {
      > .container > .svg-container {
        &.floating {
          top: 8.5%;
        }
        > svg {
          width: 12rem;
          height: 12rem;
        }
      }

      > .tempNow.large {
        position: absolute;
        top: 20%;
        left: 52%;
        transform: translateX(0%);
        font-size: 4.5rem;
        text-align: end;
        > .weatherBox-text {
          font-size: 2rem;
          font-weight: 500;
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }
      }
    }
    > .weather-effect-container {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 1;
    }

    // 부모 요소 하단에 날씨 박스
    .weatherBox-wrapper {
      position: absolute;
      bottom: 0.75rem;
      left: 28%;
      width: 70%;
      display: flex;
      gap: 0.5rem;

      .weatherBox {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        width: 50%;
        height: 7rem;
        border-radius: 8px;
        padding: 1rem;
        text-align: center;
        font-size: 0.875rem;
        background-color: var(--alpha-25);

        .weatherBox-title {
          font-size: 1.125rem;
          font-weight: 500;
          margin-bottom: 0.875rem;
        }

        .weatherBox-content {
          display: flex;
          justify-content: space-evenly;
          align-items: center;

          .weatherBox-details {
            font-size: 1rem;
            line-height: 1.5;
            > span:first-child {
              margin-right: 0.5rem;
            }
            > span:last-child {
              line-height: 1.25;
            }
          }
        }

        .weatherBox-icon {
          width: 3rem;
          height: 3rem;
          display: flex;
          justify-content: center;
          align-items: flex-end;
        }
      }
      .weatherBox.text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        > div {
          display: flex;
          justify-content: space-between;
        }
      }
    }
  }

  * {
    line-height: normal;
  }

  p {
    color: var(--filled-red) !important;
  }

  .loading {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .unit {
    font-weight: 200;
    margin-left: 0.25rem;
  }
  .seperator {
    height: 1rem;
    border-left: 0.125rem solid var(--alpha-25);
    margin-left: 0.25rem;
  }
  .title {
    height: 4rem;
    width: 100%;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    font-weight: 700;
  }
  .header {
    flex-grow: 1;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    .tempNow {
      font-size: 3.5rem;
      font-weight: 600;
      /* margin: 0.5rem 0; */
    }
    .discrict {
      font-size: 3rem;
      font-weight: 400;
      &:before {
        content: '•';
        margin-right: 1rem;
        color: var(--alpha-50);
      }
    }
  }
  .info {
    display: flex;
    flex-direction: column;
    color: var(--board);
    .tempNow {
      font-size: 1.75rem;
      font-weight: 600;
      margin-top: 0.5rem;
    }
    .discrict {
      font-size: 3rem;
      font-weight: 400;
      &:before {
        content: '•';
        margin-right: 1rem;
        color: var(--alpha-50);
      }
    }
    .maxAndMin {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .details {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      .material-symbols-rounded {
        font-size: 1.375rem;
        font-variation-settings: 'FILL' 1, 'wght' 300;
      }
      .label {
        font-size: 0.875rem;
        font-weight: 300;
      }
      .value {
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
  }
  .weatherAnimationWrapper {
    .svg-container {
      position: initial;
      &.floating {
        position: absolute;
        left: auto;
        right: 0;
        top: 17.5%;
        top: 20.5%;
      }
      &.bouncing {
        position: absolute;
        left: auto;
        right: 0;
        top: 17.5%;
        top: 22.5%;
      }
      &.sunCloud {
        top: -10%;
        right: 4%;
      }
    }
    svg {
      width: 7rem;
      height: 7rem;
    }
    .rotating-sun {
      animation: rotation 10s infinite linear;
      position: absolute;
      top: 20%;
      right: 1rem;
      right: 3.5rem;
      transform: translateY(-50%);
      z-index: 100;
    }
    .little_cloud {
      position: absolute;
      right: 3rem;
      top: 0;
      &.beside-sun {
        z-index: 999;
        right: 3rem;
        top: 4rem;
      }
    }
    .snow_cloud {
      position: absolute;
      right: 4rem;
      top: -40%;
    }
    .ico-blur {
      position: absolute;
      right: 4rem;
      top: -40%;
      opacity: 0.8;
    }
    .cloudy {
      left: 25% !important;
      top: 17%;
    }
    .snowdrop svg {
      width: 4rem;
      height: 4rem;
    }
  }
`;

const getWeatherText = (weather: string) => {
  switch (weather) {
    case 'sunny':
      return '맑음';
    case 'little_cloud':
      return '구름 적음';
    case 'cloudy':
      return '구름 많음';
    case 'blur':
      return '흐림';
    case 'rain':
      return '비';
    case 'sleet':
      return '진눈깨비';
    case 'snow':
      return '눈';
    case 'shower':
      return '소나기';
    default:
      return '';
  }
};

const getCurrentWeatherIcon = (weather: string, iconSize: number = 72) => {
  return getWeatherIconComponent(weather, iconSize);
};

export const WeatherCurrentInfo = ({ name, size, refetchInterval }: { name: string; size: 'small' | 'large'; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const {
    curWeather,
    threeDaysWeather,
    openDayWeather,
    openThreeDaysWeather,
    shortAdrs,
    refetchCurrentWeather,
    refetchThreeDaysWeather,
    refetchOpenDayWeather,
    refetchOpenThreeDaysWeather,
    isErrorCurrentWeather,
    isErrorThreeDaysWeather,
  } = useWeatherInfo({ autoRefresh: isSiteDashboard });
  const { t } = useTranslation();
  // const { weekWeather, tWeather, shortAdrs } = useWeatherInfo({ autoRefresh: refetchInterval === undefined });

  const afterDays = Array.from({ length: 3 }).map((_, index) => {
    const targetDay = dayjs().add(index + 1, 'day'); // +1 to start from tomorrow
    return {
      order: index,
      date: targetDay.date(),
      day: targetDay.format('ddd'),
      sky: '-',
      maxTemp: '0',
      minTemp: '0',
    };
  });

  // 로컬 스토리지에서 데이터 가져오기
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeatherW');
  const after2DaysLocalStorage = window.localStorage.getItem('after2Days');
  const localAfter2Days = after2DaysLocalStorage ? JSON.parse(after2DaysLocalStorage) : afterDays;
  const [after2Days, setAfter2Days] = useState<any[]>(localAfter2Days);

  const localCurrentWeather = currentWeatherLocalStorage //
    ? JSON.parse(currentWeatherLocalStorage)
    : { wsd: '0', tmp: '0', minTemp: '0', maxTemp: '0', rainPct: '0', sky: '-', pm10: '0', pm25: '0' };

  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);

  // 사이트 정보 쿼리 결과 처리
  useEffect(() => {
    if (shortAdrs) setCurrentWeather((prev: any) => ({ ...prev, district: shortAdrs }));
  }, [shortAdrs]);

  // 현재 날씨 쿼리 결과 처리
  useEffect(() => {
    if (!isErrorCurrentWeather && curWeather) {
      setCurrentWeather((prev: any) => ({ ...prev, sky: curWeather.sky, tmp: curWeather.tmp, wsd: curWeather.wsd }));
    }
  }, [isErrorCurrentWeather, curWeather]);

  useEffect(() => {
    if (currentWeather) {
      window.localStorage.setItem('currentWeatherW', JSON.stringify(currentWeather));
    }
  }, [currentWeather]);

  useEffect(() => {
    if (!isErrorThreeDaysWeather && threeDaysWeather) {
      setAfter2Days(threeDaysWeather);
    }
  }, [isErrorThreeDaysWeather, threeDaysWeather]);

  useEffect(() => {
    if (isErrorThreeDaysWeather && openDayWeather) {
      const { sky, tmp, wsd, todayTmx, todayTmn } = openDayWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd, maxTemp: todayTmx, minTemp: todayTmn }));
    }
  }, [isErrorThreeDaysWeather, openDayWeather]);

  useEffect(() => {
    if (isErrorThreeDaysWeather && openThreeDaysWeather) {
      setAfter2Days(openThreeDaysWeather);
    }
  }, [isErrorThreeDaysWeather, openThreeDaysWeather]);

  useEffect(() => {
    if (after2Days) {
      window.localStorage.setItem('after2Days', JSON.stringify(after2Days));
    }
  }, [after2Days]);

  useEffect(() => {
    if (currentWeather) {
      window.localStorage.setItem('currentWeather', JSON.stringify(currentWeather));
    }
  }, [currentWeather]);

  const onClickRefresh = () => {
    refetchCurrentWeather();
    refetchThreeDaysWeather();
    refetchOpenDayWeather();
    refetchOpenThreeDaysWeather();
  };

  const todayjs = dayjs(); // 현재 날짜와 시간을 가져옴
  let format = todayjs.format(`YYYY년 MM월 DD일 dddd`); // 기본 형식 설정

  // 언어가 영어일 경우 형식 변경
  if (i18n.language === 'en-US') {
    format = todayjs.locale('en').format(`YYYY MMMM D dddd`);
  } else {
    format = todayjs.locale('ko').format(`YYYY년 MM월 DD일 dddd`);
  }

  // SVG 표시 상태를 관리하는 상태
  const [showSvg, setShowSvg] = useState(false);

  // 날씨 배경 애니메이션
  const animateWeatherBg = (weather: string) => {
    switch (weather) {
      case 'rain': {
        const newDrops: any[] = [];
        let increment = 0;
        while (increment < 95) {
          const randoHundo = Math.floor(Math.random() * 100);
          const randoFiver = Math.floor(Math.random() * 5) + 2;
          increment += randoFiver;

          const dropStyle = {
            left: `${increment}%`,
            animationDelay: `0.${randoHundo}s`,
            animationDuration: `0.8${randoHundo}s`,
          };

          newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
        }

        return <div className='weather-effect-container'>{newDrops}</div>;
      }
      case 'shower': {
        const newDrops: any[] = [];
        let increment = 0;
        while (increment < 95) {
          const randoHundo = Math.floor(Math.random() * 100);
          const randoFiver = Math.floor(Math.random() * 5) + 2;
          increment += randoFiver;

          const dropStyle = {
            left: `${increment}%`,
            animationDelay: `0.${randoHundo}s`,
            animationDuration: `0.8${randoHundo}s`,
          };

          newDrops.push(<div className='raindrop' style={dropStyle} key={increment} />);
        }

        return <div className='weather-effect-container'>{newDrops}</div>;
      }
      case 'sleet': {
        const newDrops: any[] = [];
        let increment = 0;
        const types = ['raindrop', 'snowdrop'];

        while (increment < 100) {
          const randomType = types[Math.floor(Math.random() * types.length)];

          const randomDelay = (Math.random() * 5).toFixed(2);
          const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
          const randomLeftRight = Math.floor(Math.random() * 4);

          const snowSize = Math.floor(Math.random() * 5);
          const snowOpacity = (Math.random() * 1).toFixed(2);
          const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

          increment += randomUnit;

          let animationDuration;
          switch (randomType) {
            case 'raindrop':
              animationDuration = `${2 + Math.random()}s`;
              break;
            case 'snowdrop':
              animationDuration = `${5 + Math.random()}s`;
              break;
            default:
              animationDuration = `${5 + Math.random()}s`;
              break;
          }

          const rainParticle = {
            left: `${increment}%`,
            animationDelay: `${+randomDelay}s`,
            animationDuration,
            '--random-left-right': `${randomLeftRight}vw`,
          } as React.CSSProperties;

          const snowParticle = {
            width: `${snowSize}px`,
            height: `${snowSize}px`,
            borderRadius: `${snowSize}px`,
            backgroundColor: `#fff`,
            opacity: `${Math.max(+snowOpacity, 0.3)}`,
            left: `${increment}%`,
            animationDelay: `${+randomDelay}s`,
            animationDuration: `${randomDuration}s`,
            '--random-left-right': `${randomLeftRight}vw`,
          } as React.CSSProperties;

          newDrops.push(<div className={randomType} style={randomType === 'raindrop' ? rainParticle : snowParticle} key={increment} />);
        }

        return <div className='weather-effect-container'>{newDrops}</div>;
      }
      case 'snow': {
        const newDrops: any[] = [];
        let increment = 0;
        while (increment < 100) {
          const randomDelay = (Math.random() * 5).toFixed(2);
          const randomUnit = Math.max(Math.floor(Math.random() * 5), 1);
          const randomLeftRight = Math.floor(Math.random() * 4);

          const snowSize = Math.floor(Math.random() * 5);
          const snowOpacity = (Math.random() * 1).toFixed(2);
          const randomDuration = Math.max(Math.floor(Math.random() * 8), 2);

          increment += randomUnit;

          const snowParticle = {
            width: `${snowSize}px`,
            height: `${snowSize}px`,
            borderRadius: `${snowSize}px`,
            backgroundColor: `#fff`,
            opacity: `${Math.max(+snowOpacity, 0.3)}`,
            left: `${increment}%`,
            animationDelay: `${+randomDelay}s`,
            animationDuration: `${randomDuration}s`,
            '--random-left-right': `${randomLeftRight}vw`,
          };

          newDrops.push(<div className='snowdrop' style={snowParticle as React.CSSProperties} key={increment} />);
        }

        return <div className='weather-effect-container'>{newDrops}</div>;
      }
      default: {
        return null;
      }
    }
  };

  // SVG 표시 상태를 관리하는 상태
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSvg(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <WeatherCurrentInfoRoot className={`weatherNow medium ${size}`} style={isErrorCurrentWeather ? { position: 'relative' } : { position: 'static' }}>
      {/* {isErrorCurrentWeather ? (
        <div className='weatherError'>
          <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
        </div>
      ) : null} */}
      {currentWeather.tmp === '' ? (
        <div className='loading'>
          <PulseLoader color='rgb(255, 255, 255)' size='8px' />
        </div>
      ) : (
        <>
          <div className='info'>
            <div className='details location'>
              <span className='material-symbols-rounded'>distance</span>
              <span className='label'>{t('위치')}</span>
              <span className='value'>{t(shortAdrs)}</span>
            </div>
            <div className='maxAndMin'>
              <div className='details min'>
                <span className='material-symbols-rounded'>arrow_downward</span>
                <div className='value'>
                  {Math.floor(after2Days[0].minTemp)}
                  <span className='unit'>°</span>
                </div>
              </div>
              <span className='seperator' />
              <div className='details max'>
                <span className='material-symbols-rounded'>arrow_upward</span>
                <div className='value'>
                  {Math.floor(after2Days[0].maxTemp)}
                  <span className='unit'>°</span>
                </div>
              </div>
            </div>
            <div className='details'>
              <span className='material-symbols-rounded'>water_drop</span>
              <span className='label'>{t('강수확률')}</span>
              <span className='value'>
                {after2Days[0]?.rainPct}
                <span className='unit'>%</span>
              </span>
            </div>
            <div className='details'>
              <span className='material-symbols-rounded'>masks</span>
              <span className='label'>{t('미세먼지')}</span>
              <span className='value'>
                {currentWeather.pm10}
                <span className='unit'>㎍/㎥</span>
              </span>
            </div>
            <div className='details'>
              <span className='material-symbols-rounded'>air</span>
              <span className='label'>{t('풍속')}</span>
              <span className='value'>
                {currentWeather.wsd}
                <span className='unit'>m/s</span>
              </span>
            </div>
          </div>
          <div className='large-info-wrapper'>
            <div className='large-weather-top'>
              <div className='large-weather-animation'>{getCurrentWeatherIcon(currentWeather.sky, 100)}</div>
              <div className='tempNow'>
                <span>
                  <b>{currentWeather.tmp}°</b> / {getWeatherText(currentWeather.sky)}
                </span>
                <span className='large-date-format'>{format}</span>
              </div>
            </div>
            <div className='large-weather-bottom'>
              <div className='bottom-weather-item'>
                <div className='large-item-icon'>{getCurrentWeatherIcon(after2Days[1].sky, 72)}</div>
                <div className='large-item-details'>
                  <div className='large-item-title'>{t('내일')}</div>
                  <div className='large-item-info flex-basic'>
                    <span>{after2Days[1].minTemp}°</span>
                    <span>{after2Days[1].maxTemp}°</span>
                  </div>
                </div>
              </div>
              <div className='bottom-weather-item'>
                <div className='large-item-icon'>{getCurrentWeatherIcon(after2Days[2].sky, 72)}</div>
                <div className='large-item-details'>
                  <div className='large-item-title'>{t('모레')}</div>
                  <div className='large-item-info flex-basic'>
                    <span>{after2Days[2].minTemp}°</span>
                    <span>{after2Days[2].maxTemp}°</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {animateWeatherBg(currentWeather.sky)}
    </WeatherCurrentInfoRoot>
  );
};
